import { useI18n } from "src/utils/lni18n";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ImportXLFileCommand, EditTestDataDTO, MCTestDataDto, MCDataQuestion } from '../DomainModels';
import { useAtom } from "jotai";
import QuestionsList from "./QuestionsList";
import { MCTestService } from "./MCTestService";
import QuestionEditor from "./QuestionEditor";
import { API_HOST } from "src/utils/constants";
import { useDialog } from "src/components/Modalservice/Dialogservice";
import ImportXLDialog from "./ImportXLDialog";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import LnIcon from "src/components/LnIcon";

const MCTest = (props: { readOnly: boolean, data: EditTestDataDTO<MCTestDataDto>, onChange: (d: MCTestDataDto) => void }) => {

    const dialogPortal = useDialog();
    const { data, onChange, readOnly } = props;
    const { languageService: t } = useI18n();
    const service = useMemo(() => new MCTestService(data.TestData), []);

    const [minNumCorrect, setMinNumCorrect] = useState(Math.ceil((data.TestData.Percentage * data.TestData.Questions.length) / 100));

    const [mcData] = useAtom(service.datatom);
    const [currentQuestionsData, setCurrentQuestionsData] = useState<MCDataQuestion | undefined>(undefined);


    const getPercentFromNumCorrect = useCallback((num: number, numQuestions: number) => {
        let percent = Math.floor((num / numQuestions) * 100);
        const add = 5 - percent % 5;
        if (add !== 0 && add !== 5) { percent = Math.min(percent + add, 100); }
        return percent;
    }, []);

    const handleOnEdit = (index: number) => {
        const currentQuestion = service.setCurrentQuestion(index);
        setCurrentQuestionsData(currentQuestion)
        
        
    }

    const getMinNumCorrect = useCallback(() => {
        return minNumCorrect;
    }, [minNumCorrect]);

    useEffect(() => {
        const total = mcData.Questions.length || 1;
        const minNumCorrect = getMinNumCorrect();
        const minPercentCorrect = getPercentFromNumCorrect(minNumCorrect, total);
        if (mcData.Percentage < minPercentCorrect) {
            const minNum = Math.ceil((mcData.Percentage * total) / 100);
            setMinNumCorrect(minNum);
        }
    }, [mcData, mcData.Questions.length, getMinNumCorrect]);



    const importXL = () => {

        dialogPortal({
            factory: (onSubmit, onCancel) => {
                return <ImportXLDialog onSubmit={onSubmit} onCancel={onCancel} />
            },
            size: "md"
        }).then((d: { key: string, replace: boolean }) => {
            const postData: ImportXLFileCommand = {
                FileName: d.key,
                PartId: data.PartID,
                TestId: data.TestId,
                Replace: d.replace
            }
            service.importFromXL(postData);
        }).catch(() => { });

    }


    const getLink = `${API_HOST}/api/author/coursepart/${data.PartID}/test/${data.TestId}/ExportToSpreadsheet`

    if (!data || !mcData) return null;

    function handleOnChange(d: MCTestDataDto): void {
        onChange(d);
    }

    function handleDelete(index: number) {
        if (currentQuestionsData && currentQuestionsData.Index === index) {
            setCurrentQuestionsData(undefined);
        }
        var data = service.deleteQuestion(index);
        onChange(data);
    }

    function setPercentage(percent: number) {
        var data = service.setPercentage(Math.min(percent, 100));
        onChange(data);
        setMinNumCorrect(Math.ceil((data.Percentage * data.Questions.length) / 100));
    }



    function setMinCorrectAnswers(num: number) {

        if (num >= mcData.Questions.length) {
            setPercentage(100);
            setMinNumCorrect(mcData.Questions.length);
            return;
        }

        let percent = getPercentFromNumCorrect(num, mcData.Questions.length);

        var data = service.setPercentage(percent);
        onChange(data);
        setMinNumCorrect(num);
    }

    return <div className="mt-5" >
        <div className="row mb-3">
            <div className="col-sm-4 col-md-3 mb-3">
                <table className="table table-sm table-borderless">
                    <tbody>
                        <tr>
                            <td className="font-weight-bold">
                                <label htmlFor="percentage">{t.getText("percentage.required")}</label>
                            </td>
                            <td>
                                <input id="percentage"
                                    disabled={readOnly}
                                    className="form-control d-inline-block w-auto"
                                    type="number" min={10} max={100} step={5} value={mcData.Percentage}
                                    onChange={(e) => setPercentage(+e.currentTarget.value)}
                                    aria-label={t.getText("percentage.required")}
                                />
                            </td>

                        </tr>
                        <tr>
                            <td className="font-weight-bold">
                                <label htmlFor="numCorrect">{t.getText("min.num.correct.answers")}</label>
                            </td>
                            <td>
                                <input id="numCorrect" className="form-control d-inline-block w-100"
                                    disabled={readOnly}
                                    type="number" min={1} max={mcData.Questions.length} step={1} value={minNumCorrect}
                                    onChange={(e) => setMinCorrectAnswers(+e.currentTarget.value)}
                                    aria-label={t.getText("min.num.correct.answers")}
                                />

                            </td>
                        </tr>
                    </tbody>
                </table>

            </div>

            {!readOnly &&
                <div className="pl-5 col-sm-8 col-md-9">
                    <button disabled={readOnly} type='button' onClick={importXL} className='btn btn-warning btn-small mr-3  mb-3' >{t.getText("import.from.xl")}</button>
                    <a target="_blank" href={getLink} className='btn btn-primary btn-small mb-3' >{t.getText("export.as.xl")}</a>
                </div>
            }
        </div>

        <PanelGroup autoSaveId={`mc_${data.TestId}`} direction="horizontal">
            <Panel defaultSize={25} className="border">
                <QuestionsList readOnly={readOnly} service={service}
                    data={mcData.Questions} onDelete={(i) => handleDelete(i)}
                    onEdit={(i) => handleOnEdit(i)} />
            </Panel>
            <PanelResizeHandle className="resizeHandle" >
                <LnIcon name="more" />
            </PanelResizeHandle>
            <Panel className="border">
                {currentQuestionsData && <QuestionEditor  readOnly={readOnly}
                    service={service} data={currentQuestionsData} onChange={handleOnChange} />}
            </Panel>

        </PanelGroup>

    </div>

}

export default MCTest;

